import React, { useState, useEffect } from "react";
import { api } from "../utils/api";
import { PageHeader, Card } from "../components/ui/Card";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { EmptyState, LoadingRow } from "../components/ui/EmptyState";
import { colors, radius } from "../components/ui/tokens";
import { AlertTriangleIcon, StarIcon } from "../components/ui/icons";

const FILTERS = [
  { key: "all", label: "Tous" },
  { key: "pending", label: "En attente" },
  { key: "grave", label: "Graves (< 4)" },
  { key: "warning", label: "Avertissements" },
  { key: "resolved", label: "Résolus" },
];

export default function Reports() {
  const [reports, setReports] = useState<any[]>([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/ratings").then(data => {
      if (data && Array.isArray(data.reports)) setReports(data.reports);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const typeOf = (r: any) => (r.serious_reason || r.average_score < 4 ? "grave" : "warning");
  const reasonOf = (r: any) => r.serious_reason || r.report_reason || r.low_score_reason || r.comment || "—";

  const filtered = reports.filter(r =>
    filter === "all" ||
    (filter === "resolved" && r.is_resolved) ||
    (filter === "pending" && !r.is_resolved) ||
    (filter !== "resolved" && filter !== "pending" && typeOf(r) === filter)
  );

  const resolve = (id: string) => {
    api.put(`/ratings/${id}/resolve`, {}).then(() => {
      setReports(prev => prev.map(r => r.id === id ? { ...r, is_resolved: true } : r));
    }).catch(() => {});
  };

  const ban = (userId: string) => {
    if (!window.confirm("Bannir cet utilisateur ? Il ne pourra plus se connecter.")) return;
    api.put(`/users/${userId}/ban`, {}).catch(() => {});
  };

  return (
    <div style={{ padding: 32 }}>
      <PageHeader title="Signalements" subtitle="Évaluations et signalements à traiter" />

      <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
        {FILTERS.map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            style={{
              padding: "8px 16px", borderRadius: radius.pill,
              border: filter === f.key ? "none" : `1px solid ${colors.borderStrong}`,
              backgroundColor: filter === f.key ? colors.text : "white",
              color: filter === f.key ? "white" : colors.textMuted,
              fontSize: 13, fontWeight: 600, cursor: "pointer",
            }}
          >{f.label}</button>
        ))}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {loading && <Card><LoadingRow /></Card>}
        {!loading && filtered.length === 0 && <Card><EmptyState title="Aucun signalement" icon={<AlertTriangleIcon size={40} color={colors.textFaint} />} /></Card>}
        {filtered.map(report => {
          const type = typeOf(report);
          return (
            <Card key={report.id} style={{ border: `1px solid ${type === "grave" ? colors.dangerSoft : colors.border}`, display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{
                width: 44, height: 44, borderRadius: "50%",
                backgroundColor: type === "grave" ? colors.dangerSoft : colors.warningSoft,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>
                <AlertTriangleIcon size={20} color={type === "grave" ? colors.danger : colors.warning} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                  <span style={{ fontWeight: 700, fontSize: 14, color: colors.text }}>{report.rater_name}</span>
                  <span style={{ color: colors.textFaint, fontSize: 13 }}>→</span>
                  <span style={{ fontWeight: 700, fontSize: 14, color: colors.text }}>{report.rated_name}</span>
                  <Badge tone={report.average_score < 4 ? "danger" : "warning"}><StarIcon size={10} /> {report.average_score}/10</Badge>
                </div>
                <div style={{ fontSize: 13, color: colors.textMuted, marginBottom: 4 }}>{reasonOf(report)}</div>
                <div style={{ fontSize: 11.5, color: colors.textFaint }}>{report.publication_title || "Activité"} · {new Date(report.created_at).toLocaleDateString("fr-FR")}</div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-end", flexShrink: 0 }}>
                <Badge tone={report.is_resolved ? "success" : "danger"} dot>{report.is_resolved ? "Résolu" : "En attente"}</Badge>
                {!report.is_resolved && (
                  <div style={{ display: "flex", gap: 6 }}>
                    <Button small variant="secondary" style={{ backgroundColor: colors.successSoft, color: colors.success }} onClick={() => resolve(report.id)}>Résoudre</Button>
                    <Button small variant="danger" onClick={() => ban(report.rated_id)}>Bannir</Button>
                  </div>
                )}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
